import React, { useEffect, useState } from "react";
import SuperUserHeader from "../../components/SuperUserHeader";
import SuperUserSideMenu from "../../components/SuperUserSideMenu";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import ReturnButton from "../../components/ReturnButton";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import { formatDate, formatSttCustomerCompany, formatIdWithLength, formatDisplayAllowedIP } from "../../utils/format";
import {
    setSuccess,
    setError,
    getCustomerCompanyDetail,
} from "../../reducers/customerCompany";
import NotFoundPage from "../../pages/NotFoundPage";
import parse, {Element} from "html-react-parser";
import { options } from "marked";

const CompanyDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const {
        loading,
        success,
        error,
        actionName,
        actionStatus,
        detailData,
    } = useSelector((state: any) => state.customerCompany);
    const { id } = useParams<{ id: string }>();
        
    useEffect(() => {
        if(id){
            const data = {
                id: Number(id),
            }
            dispatch(getCustomerCompanyDetail(data));
        }
    }, [id]);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    useEffect(() => {
        if(error) {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={error}
                    variant={"error"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setError(null));
        }
        else if(success) {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={success}
                    variant={"success"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setSuccess(null));
        }
    }, [error, success, actionName]);

    if (!detailData || (Array.isArray(detailData) && detailData.length === 0) || Object.keys(detailData).length === 0) {
        if(actionName == "getCustomerCompanyDetail" && actionStatus == "rejected") return <NotFoundPage />;
        return ;
    }

    return (
        <div className="bg-alu-base h-dvh">
        <SuperUserHeader />
        <div className="mt-6 px-6 flex gap-6 relative bg-alu-base pb-8">
            <SuperUserSideMenu />
            <div className="flex-1">
            <div className="mb-6 flex items-center gap-4">
                <ReturnButton onClick={() => navigate("/control/company")} />
                <span className="text-sm alu-text-base-1">/</span>
                <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                    {detailData.name + " の詳細"}
                </h3>
            </div>

            <div className="mb-2">
                <CustomButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/control/company/edit/" + detailData.id)}
                    customStyle="!min-w-[63px]"
                >
                    編集
                </CustomButton>
            </div>
            <div className="bg-white rounded-lg p-4">
                <form className="w-full">
                    <div className="w-[50%] max-w-[464px]">
                        <div className="grid grid-cols-[112px_1fr] items-center gap-4 mb-4 py-[0.3125rem]">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">企業ID</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{formatIdWithLength(detailData.id)}</p>
                        </div>
                        <div className="grid grid-cols-[112px_1fr] items-center gap-4 mb-4 py-[0.3125rem]">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">企業名</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{detailData.name}</p>
                        </div>
                        <div className="grid grid-cols-[112px_1fr] items-center gap-4 mb-4 py-[0.3125rem]">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">契約開始日</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{formatDate(detailData.contract_started_at)}</p>
                        </div>
                        <div className="grid grid-cols-[112px_1fr] items-center gap-4 mb-4 py-[0.3125rem]">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">契約期限</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{formatDate(detailData.expired_at)}</p>
                        </div>
                        <div className="grid grid-cols-[112px_1fr] items-center gap-4 mb-4 py-[0.3125rem]">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">状態</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{formatSttCustomerCompany(detailData.is_contract_valid)}</p>
                        </div>
                        <div className="grid grid-cols-[112px_1fr] items-center gap-4 mb-4 py-[0.3125rem]">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">所属と役職</p>
                            <div className="w-full flex items-center gap-4">
                                <span 
                                    className="btn-alu-text text-sm px-2 py-[2px] hover:cursor-pointer"
                                    onClick={() => navigate("/control/department/register/" + detailData.id)}
                                >
                                    所属の登録
                                </span>
                                <span className="alu-text-base-2 text-sm">｜</span>
                                <span 
                                    className="btn-alu-text text-sm px-2 py-[2px] hover:cursor-pointer"
                                    onClick={() => navigate("/control/position/register/" + detailData.id)}
                                >
                                    役職の登録
                                </span>
                            </div>
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">IP アクセス制限<span className="font-normal">（許可する IP）</span></p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{formatDisplayAllowedIP(detailData.allowed_ip)}</p>
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">企業情報</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{detailData.description ? detailData.description : 'ー'}</p>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">個人情報保護方針</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2 whitespace-pre-line">{detailData.privacy_policy ? detailData.privacy_policy : 'ー'}</p>
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">企業ロゴ</p>
                            { detailData.logo ?
                                <div className="w-fit h-20">
                                    <img
                                    className="w-full h-full object-contain"
                                    src={detailData.logo}
                                    alt="logo"
                                    />
                                </div>
                                :
                                <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">ー</p>
                            }
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2">企業URL</p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{detailData.url_slug ? detailData.url_slug : 'ー'}</p>
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">アルムナイ登録条件 <span className="font-normal">{detailData.is_public_alumni ? "（ 公開 ）" : "（ 非公開 ）"}</span></p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{detailData.alumni_registration_condition ? detailData.alumni_registration_condition : 'ー'}</p>
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">社外タレント登録条件 <span className="font-normal">{detailData.is_public_external_talent ? "（ 公開 ）" : "（ 非公開 ）"}</span></p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{detailData.other_talent_registration_condition ? detailData.other_talent_registration_condition : 'ー'}</p>
                        </div>
                        <div className="mb-4 whitespace-pre-wrap">
                            <p className="text-sm font-bold leading-[1.3125] alu-text-base-2 mb-2">社内タレント登録条件 <span className="font-normal">{detailData.is_public_internal_talent ? "（ 公開 ）" : "（ 非公開 ）"}</span></p>
                            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{detailData.internal_talent_registration_condition ? detailData.internal_talent_registration_condition : 'ー'}</p>
                        </div>
                    </div>
                </form>
            </div>
            </div>
        </div>
        </div>
    );
};

export default CompanyDetail;
